import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  ChevronLeft, Loader2, Sparkles, Stethoscope, FlaskConical, Target,
  HeartPulse, CalendarCheck, Save, AlertCircle, Pencil, Printer,
  Plus, Activity, TrendingUp, Trash2, GitBranch, Download,
} from "lucide-react";
import Timeline, { ORDER } from "@/components/Timeline";
import { ProbBar, SectionTitle, CategoryBadge } from "@/components/StepBits";
import NextAppointment from "@/components/NextAppointment";
import {
  EditProvisional, EditInvestigations, EditFinal, EditCarePlan, EditFollowUp, RecordVisit,
} from "@/components/EditDialogs";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

const EditBtn = ({ onClick, testid }) => (
  <Button variant="outline" size="sm" onClick={onClick} data-testid={testid} className="gap-1.5">
    <Pencil className="h-3.5 w-3.5" /> Edit
  </Button>
);

function exportCase(c) {
  const safe = { ...c };
  delete safe._id;
  const blob = new Blob([JSON.stringify(safe, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const name = (c.patient?.name || "case").replace(/[^a-z0-9]+/gi, "_").toLowerCase();
  a.href = url;
  a.download = `oncodx_${name}_${c.id.slice(0, 8)}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Build numeric trend series from visit readings (only params with >= 2 numeric points).
function buildTrends(c) {
  const visits = c.visits || [];
  const series = {};
  visits.forEach((v) => {
    (v.readings || []).forEach((r) => {
      const num = parseFloat(String(r.value).replace(/[^0-9.\-]/g, ""));
      if (!isNaN(num) && String(r.value).match(/[0-9]/)) {
        if (!series[r.parameter]) series[r.parameter] = [];
        series[r.parameter].push({ date: v.date, value: num });
      }
    });
  });
  return Object.entries(series)
    .filter(([, pts]) => pts.length >= 2)
    .map(([parameter, points]) => ({ parameter, points }));
}


export default function CaseWorkspace() {
  const { role, caseId } = useParams();
  const navigate = useNavigate();
  const isDoctor = role === "doctor";
  const [c, setC] = useState(null);
  const [active, setActive] = useState("history");
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const data = await api.getCase(caseId);
      setC(data);
      setActive(data.status || "history");
    } catch (e) {
      toast.error("Case not found");
      navigate(`/${role}/cases`);
    }
  };

  useEffect(() => {
    load(); // eslint-disable-next-line
  }, [caseId]);

  const run = async (label, fn, goTo) => {
    setBusy(label);
    try {
      const updated = await fn();
      setC(updated);
      if (goTo) setActive(goTo);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The assistant could not complete this step. Retry.");
    } finally {
      setBusy("");
    }
  };

  if (!c) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const AIButton = ({ testid, label, loadingLabel, icon: Icon, onClick }) => (
    <Button data-testid={testid} onClick={onClick} disabled={!!busy} className="gap-2">
      {busy === testid ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
      {busy === testid ? loadingLabel : label}
    </Button>
  );

  // Furthest reachable step. Once a follow-up plan exists, unlock the Visits step.
  let reachedIdx = ORDER.indexOf(c.status);
  if ((c.follow_up?.schedule?.length || c.follow_up?.monitoring_parameters?.length) && reachedIdx < ORDER.indexOf("visits")) {
    reachedIdx = ORDER.indexOf("visits");
  }

  return (
    <div className="min-h-screen pb-24">
      <div className="mx-auto flex max-w-6xl flex-col gap-6 px-6 pt-8 lg:flex-row">
        {/* Sidebar */}
        <aside className="lg:w-64 lg:shrink-0">
          <button
            data-testid="workspace-back"
            onClick={() => navigate(`/${role}/cases`)}
            className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" /> All cases
          </button>
          <Card className="p-4">
            <p className="font-heading text-lg font-semibold text-foreground">{c.patient?.name}</p>
            <p className="text-xs text-muted-foreground">
              {[c.patient?.age && `${c.patient.age} yrs`, c.patient?.sex].filter(Boolean).join(" · ")}
            </p>
            <Separator className="my-3" />
            <Timeline reachedIdx={reachedIdx} active={active} onSelect={setActive} />
            <Separator className="my-3" />
            <NextAppointment c={c} setC={setC} isDoctor={isDoctor} />
            <div className="mt-3 flex flex-col gap-2">
              <Button
                data-testid="doctor-findings-btn"
                variant="outline"
                onClick={() => navigate(`/${role}/cases/${c.id}/findings`)}
                className="w-full gap-2"
              >
                <Stethoscope className="h-4 w-4" /> Doctor's Findings
              </Button>
              <Button
                data-testid="journey-view-btn"
                variant="outline"
                onClick={() => navigate(`/${role}/cases/${c.id}/journey`)}
                className="w-full gap-2"
              >
                <GitBranch className="h-4 w-4" /> Full Journey
              </Button>
              <Button
                data-testid="print-summary-btn"
                variant="outline"
                onClick={() => navigate(`/${role}/cases/${c.id}/summary`)}
                className="w-full gap-2"
              >
                <Printer className="h-4 w-4" /> Print / PDF Summary
              </Button>
              <Button
                data-testid="export-case-btn"
                variant="outline"
                onClick={() => exportCase(c)}
                className="w-full gap-2"
              >
                <Download className="h-4 w-4" /> Export Case (JSON)
              </Button>
            </div>
          </Card>
        </aside>

        {/* Main */}
        <main className="min-w-0 flex-1">
          <div key={active} className="animate-fade-in-up">
            {active === "history" && <HistoryPanel c={c} AIButton={AIButton} run={run} />}
            {active === "questions" && (
              <QuestionsPanel c={c} setC={setC} isDoctor={isDoctor} AIButton={AIButton} run={run} />
            )}
            {active === "provisional" && <ProvisionalPanel c={c} setC={setC} isDoctor={isDoctor} AIButton={AIButton} run={run} />}
            {active === "investigations" && (
              <InvestigationsPanel c={c} setC={setC} isDoctor={isDoctor} AIButton={AIButton} run={run} />
            )}
            {active === "final" && <FinalPanel c={c} setC={setC} isDoctor={isDoctor} AIButton={AIButton} run={run} />}
            {active === "care_plan" && <CarePlanPanel c={c} setC={setC} isDoctor={isDoctor} AIButton={AIButton} run={run} />}
            {active === "follow_up" && <FollowUpPanel c={c} setC={setC} isDoctor={isDoctor} AIButton={AIButton} run={run} />}
            {active === "visits" && <VisitsPanel c={c} setC={setC} isDoctor={isDoctor} />}
          </div>
        </main>
      </div>
    </div>
  );
}

/* ---------------- Step 1: History ---------------- */
function HistoryPanel({ c, AIButton, run }) {
  const rows = [
    ["Chief complaint", c.chief_complaint],
    ["History of presenting illness", c.hpi],
    ["Past medical history", c.past_history],
    ["Medications", c.medications],
    ["Family history", c.family_history],
    ["Social history", c.social_history],
  ];
  return (
    <Card className="p-7">
      <SectionTitle sub="The clinical story captured at intake. Next, the assistant asks focused questions.">
        Patient History
      </SectionTitle>
      <div className="flex flex-col divide-y divide-border">
        {rows.map(([k, v]) => (
          <div key={k} className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-[220px_1fr]">
            <span className="text-sm font-semibold text-muted-foreground">{k}</span>
            <span className="text-sm leading-relaxed text-foreground">{v || "—"}</span>
          </div>
        ))}
      </div>
      <div className="mt-6">
        <AIButton
          testid="gen-questions-btn"
          label="Generate clarifying questions"
          loadingLabel="Thinking…"
          icon={Sparkles}
          onClick={() => run("gen-questions-btn", () => api.generateQuestions(c.id), "questions")}
        />
      </div>
    </Card>
  );
}

/* ---------------- Step 2: Questions ---------------- */
function QuestionsPanel({ c, setC, isDoctor, AIButton, run }) {
  const [answers, setAnswers] = useState(c.answers || []);
  // Sync local draft when the case's answers change.
  useEffect(() => setAnswers(c.answers || []), [c.answers]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (i, val) => {
    const next = [...answers];
    next[i] = { ...next[i], answer: val };
    setAnswers(next);
  };

  const save = async () => {
    try {
      const updated = await api.saveAnswers(c.id, answers);
      setC(updated);
      toast.success("Answers saved");
    } catch (e) {
      toast.error("Could not save answers");
    }
  };

  if (!c.questions?.length) {
    return (
      <Card className="p-7">
        <SectionTitle>Clarifying Questions</SectionTitle>
        <EmptyHint>No questions yet. Go back to History and generate them.</EmptyHint>
      </Card>
    );
  }

  return (
    <Card className="p-7">
      <SectionTitle sub="Relevant questions a clinician would ask to sharpen the differential. Fill in the answers.">
        Clarifying Questions
      </SectionTitle>
      <div className="flex flex-col gap-5">
        {answers.map((qa, i) => (
          <div key={i} className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-foreground">
              {i + 1}. {qa.question}
            </label>
            <Textarea
              data-testid={`answer-input-${i}`}
              value={qa.answer}
              onChange={(e) => update(i, e.target.value)}
              rows={2}
              placeholder="Patient's answer…"
            />
          </div>
        ))}
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        <Button data-testid="save-answers-btn" variant="outline" onClick={save} className="gap-2">
          <Save className="h-4 w-4" /> Save answers
        </Button>
        <AIButton
          testid="gen-provisional-btn"
          label="Build provisional diagnosis"
          loadingLabel="Reasoning…"
          icon={Stethoscope}
          onClick={async () => {
            await api.saveAnswers(c.id, answers);
            await run("gen-provisional-btn", () => api.provisional(c.id), "provisional");
          }}
        />
      </div>
    </Card>
  );
}

/* ---------------- Step 3: Provisional ---------------- */
function ProvisionalPanel({ c, setC, isDoctor, AIButton, run }) {
  const [edit, setEdit] = useState(false);
  const dx = c.provisional_diagnoses || [];
  if (!dx.length) {
    return (
      <Card className="p-7">
        <SectionTitle>Provisional Differential Diagnosis</SectionTitle>
        <EmptyHint>Answer the clarifying questions, then build the provisional diagnosis.</EmptyHint>
        <div className="mt-6">
          <AIButton testid="gen-provisional-btn2" label="Build provisional diagnosis" loadingLabel="Reasoning…"
            icon={Stethoscope} onClick={() => run("gen-provisional-btn2", () => api.provisional(c.id), "provisional")} />
        </div>
      </Card>
    );
  }
  return (
    <Card className="p-7">
      <div className="flex items-start justify-between gap-3">
        <SectionTitle sub={c.impression}>Provisional Differential Diagnosis</SectionTitle>
        {isDoctor && <EditBtn testid="edit-provisional-btn" onClick={() => setEdit(true)} />}
      </div>
      <div className="flex flex-col gap-3" data-testid="provisional-list">
        {dx.map((d, i) => (
          <ProbBar key={i} {...d} />
        ))}
      </div>
      <div className="mt-6">
        <AIButton testid="gen-investigations-btn" label="Order investigations" loadingLabel="Selecting tests…"
          icon={FlaskConical} onClick={() => run("gen-investigations-btn", () => api.investigations(c.id), "investigations")} />
      </div>
      <EditProvisional c={c} setC={setC} open={edit} onClose={() => setEdit(false)} />
    </Card>
  );
}

/* ---------------- Step 4: Investigations ---------------- */
function InvestigationsPanel({ c, setC, isDoctor, AIButton, run }) {
  const [results, setResults] = useState(c.investigation_results || []);
  const [edit, setEdit] = useState(false);
  // Sync local draft when the case's results change.
  useEffect(() => setResults(c.investigation_results || []), [c.investigation_results]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (i, val) => {
    const next = [...results];
    next[i] = { ...next[i], result: val };
    setResults(next);
  };
  const save = async () => {
    try {
      const updated = await api.saveResults(c.id, results);
      setC(updated);
      toast.success("Results saved");
    } catch (e) {
      toast.error("Could not save results");
    }
  };

  if (!c.investigations?.length) {
    return (
      <Card className="p-7">
        <SectionTitle>Investigations</SectionTitle>
        <EmptyHint>No investigations ordered yet.</EmptyHint>
        <div className="mt-6">
          <AIButton testid="gen-investigations-btn2" label="Order investigations" loadingLabel="Selecting tests…"
            icon={FlaskConical} onClick={() => run("gen-investigations-btn2", () => api.investigations(c.id), "investigations")} />
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-7">
      <div className="flex items-start justify-between gap-3">
        <SectionTitle sub="Appropriate workup to confirm or exclude the differential. Enter results as they return.">
          Investigations & Results
        </SectionTitle>
        {isDoctor && <EditBtn testid="edit-investigations-btn" onClick={() => setEdit(true)} />}
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[42%]">Investigation</TableHead>
            <TableHead className="w-[14%]">Type</TableHead>
            <TableHead>Result</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {c.investigations.map((inv, i) => (
            <TableRow key={i}>
              <TableCell className="align-top">
                <p className="font-medium text-foreground">{inv.name}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{inv.rationale}</p>
              </TableCell>
              <TableCell className="align-top">
                <CategoryBadge>{inv.category || "Other"}</CategoryBadge>
              </TableCell>
              <TableCell className="align-top">
                <Input
                  data-testid={`result-input-${i}`}
                  value={results[i]?.result || ""}
                  onChange={(e) => update(i, e.target.value)}
                  placeholder="Enter finding…"
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="mt-6 flex flex-wrap gap-3">
        <Button data-testid="save-results-btn" variant="outline" onClick={save} className="gap-2">
          <Save className="h-4 w-4" /> Save results
        </Button>
        <AIButton testid="gen-final-btn" label="Arrive at final diagnosis" loadingLabel="Integrating results…"
          icon={Target}
          onClick={async () => {
            await api.saveResults(c.id, results);
            await run("gen-final-btn", () => api.finalDiagnosis(c.id), "final");
          }} />
      </div>
      <EditInvestigations c={c} setC={setC} open={edit} onClose={() => setEdit(false)} />
    </Card>
  );
}

/* ---------------- Step 5: Final diagnosis ---------------- */
function FinalPanel({ c, setC, isDoctor, AIButton, run }) {
  const [edit, setEdit] = useState(false);
  const fd = c.final_diagnosis || {};
  if (!fd.name) {
    return (
      <Card className="p-7">
        <SectionTitle>Final Diagnosis</SectionTitle>
        <EmptyHint>Enter investigation results, then integrate them into a final diagnosis.</EmptyHint>
        <div className="mt-6">
          <AIButton testid="gen-final-btn2" label="Arrive at final diagnosis" loadingLabel="Integrating…"
            icon={Target} onClick={() => run("gen-final-btn2", () => api.finalDiagnosis(c.id), "final")} />
        </div>
      </Card>
    );
  }
  const conf = { High: "bg-primary text-primary-foreground", Moderate: "bg-accent text-accent-foreground", Low: "bg-secondary text-secondary-foreground" };
  return (
    <Card className="p-7">
      <div className="flex items-start justify-between gap-3">
        <SectionTitle>Final Diagnosis</SectionTitle>
        {isDoctor && <EditBtn testid="edit-final-btn" onClick={() => setEdit(true)} />}
      </div>
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-6" data-testid="final-diagnosis">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="font-heading text-2xl font-semibold text-primary">{fd.name}</h3>
          {fd.stage && fd.stage !== "N/A" && <Badge variant="outline">{fd.stage}</Badge>}
          {fd.confidence && <Badge className={conf[fd.confidence] || ""}>{fd.confidence} confidence</Badge>}
        </div>
        <p className="mt-3 text-sm leading-relaxed text-foreground">{fd.rationale}</p>
      </div>

      {c.updated_diagnoses?.length > 0 && (
        <div className="mt-6">
          <p className="mb-3 text-sm font-semibold text-muted-foreground">Updated differential after results</p>
          <div className="flex flex-col gap-3">
            {c.updated_diagnoses.map((d, i) => <ProbBar key={i} {...d} />)}
          </div>
        </div>
      )}

      <div className="mt-6">
        <AIButton testid="gen-careplan-btn" label="Build care plan & prognosis" loadingLabel="Planning treatment…"
          icon={HeartPulse} onClick={() => run("gen-careplan-btn", () => api.carePlan(c.id), "care_plan")} />
      </div>
      <EditFinal c={c} setC={setC} open={edit} onClose={() => setEdit(false)} />
    </Card>
  );
}

/* ---------------- Step 6: Care plan ---------------- */
function CarePlanPanel({ c, setC, isDoctor, AIButton, run }) {
  const [edit, setEdit] = useState(false);
  const cp = c.care_plan || {};
  if (!cp.treatments) {
    return (
      <Card className="p-7">
        <SectionTitle>Care Plan & Prognosis</SectionTitle>
        <EmptyHint>Confirm the final diagnosis, then build the care plan.</EmptyHint>
        <div className="mt-6">
          <AIButton testid="gen-careplan-btn2" label="Build care plan & prognosis" loadingLabel="Planning…"
            icon={HeartPulse} onClick={() => run("gen-careplan-btn2", () => api.carePlan(c.id), "care_plan")} />
        </div>
      </Card>
    );
  }
  const pg = cp.prognosis || {};
  return (
    <Card className="p-7">
      <div className="flex items-start justify-between gap-3">
        <SectionTitle sub="Evidence-based plan grounded in DeVita's Oncology.">Care Plan & Prognosis</SectionTitle>
        {isDoctor && <EditBtn testid="edit-careplan-btn" onClick={() => setEdit(true)} />}
      </div>
      {cp.goals && (
        <div className="mb-5 rounded-md border border-border bg-muted/50 p-4">
          <p className="text-sm font-semibold text-foreground">Goals of care</p>
          <p className="mt-1 text-sm text-muted-foreground">{cp.goals}</p>
        </div>
      )}
      <div className="flex flex-col gap-3" data-testid="treatment-list">
        {cp.treatments.map((t, i) => (
          <div key={i} className="rounded-md border border-border bg-card p-4">
            <div className="flex items-center gap-2">
              <Badge className="bg-primary text-primary-foreground">{t.modality}</Badge>
            </div>
            <p className="mt-2 text-sm leading-relaxed text-foreground">{t.detail}</p>
            {t.evidence && (
              <p className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{t.evidence}</span>
              </p>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-lg border border-accent/40 bg-accent/10 p-5" data-testid="prognosis-block">
        <p className="font-heading text-lg font-semibold text-foreground">Prognosis</p>
        <p className="mt-1 text-sm text-foreground">{pg.outlook}</p>
        {pg.five_year_survival && (
          <p className="mt-2 text-sm"><span className="font-semibold">5-year outlook: </span>{pg.five_year_survival}</p>
        )}
        {pg.key_factors?.length > 0 && (
          <ul className="mt-2 list-inside list-disc text-sm text-muted-foreground">
            {pg.key_factors.map((f, i) => <li key={i}>{f}</li>)}
          </ul>
        )}
      </div>

      <div className="mt-6">
        <AIButton testid="gen-followup-btn" label="Create follow-up plan" loadingLabel="Scheduling…"
          icon={CalendarCheck} onClick={() => run("gen-followup-btn", () => api.followUp(c.id), "follow_up")} />
      </div>
      <EditCarePlan c={c} setC={setC} open={edit} onClose={() => setEdit(false)} />
    </Card>
  );
}

/* ---------------- Step 7: Follow-up ---------------- */
function FollowUpPanel({ c, setC, isDoctor, AIButton, run }) {
  const [edit, setEdit] = useState(false);
  const fu = c.follow_up || {};
  if (!fu.schedule) {
    return (
      <Card className="p-7">
        <SectionTitle>Follow-up & Monitoring</SectionTitle>
        <EmptyHint>Build the care plan first, then generate the follow-up plan.</EmptyHint>
        <div className="mt-6">
          <AIButton testid="gen-followup-btn2" label="Create follow-up plan" loadingLabel="Scheduling…"
            icon={CalendarCheck} onClick={() => run("gen-followup-btn2", () => api.followUp(c.id), "follow_up")} />
        </div>
      </Card>
    );
  }
  return (
    <Card className="p-7">
      <div className="flex items-start justify-between gap-3">
        <SectionTitle sub="Parameters to monitor and the schedule until final disposal.">Follow-up & Monitoring</SectionTitle>
        {isDoctor && <EditBtn testid="edit-followup-btn" onClick={() => setEdit(true)} />}
      </div>

      {fu.monitoring_parameters?.length > 0 && (
        <>
          <p className="mb-2 text-sm font-semibold text-muted-foreground">Monitoring parameters</p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Parameter</TableHead>
                <TableHead>Frequency</TableHead>
                <TableHead>Watch for</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fu.monitoring_parameters.map((p, i) => (
                <TableRow key={i}>
                  <TableCell className="font-medium">{p.parameter}</TableCell>
                  <TableCell>{p.frequency}</TableCell>
                  <TableCell className="text-muted-foreground">{p.target}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      {fu.schedule?.length > 0 && (
        <div className="mt-6">
          <p className="mb-3 text-sm font-semibold text-muted-foreground">Schedule</p>
          <div className="flex flex-col gap-3 border-l-2 border-primary/30 pl-5">
            {fu.schedule.map((s, i) => (
              <div key={i} className="relative">
                <span className="absolute -left-[26px] top-1 h-3 w-3 rounded-full border-2 border-primary bg-card" />
                <p className="text-sm font-semibold text-primary">{s.timepoint}</p>
                <p className="text-sm text-foreground">{s.action}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {fu.red_flags?.length > 0 && (
        <div className="mt-6 rounded-md border border-destructive/30 bg-destructive/5 p-4">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-destructive">
            <AlertCircle className="h-4 w-4" /> Return urgently if
          </p>
          <ul className="mt-2 list-inside list-disc text-sm text-foreground">
            {fu.red_flags.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}

      {fu.disposition_criteria && (
        <div className="mt-6 rounded-md border border-border bg-muted/50 p-4">
          <p className="text-sm font-semibold text-foreground">Final disposal / remission criteria</p>
          <p className="mt-1 text-sm text-muted-foreground">{fu.disposition_criteria}</p>
        </div>
      )}
      <EditFollowUp c={c} setC={setC} open={edit} onClose={() => setEdit(false)} />
    </Card>
  );
}

/* ---------------- Step 8: Visits & Prognosis ---------------- */
function VisitsPanel({ c, setC, isDoctor }) {
  const [open, setOpen] = useState(false);
  const timeline = c.prognosis_timeline || [];
  const visits = c.visits || [];
  const trends = useMemo(() => buildTrends(c), [c]);

  const statusColor = {
    Improving: "bg-primary text-primary-foreground",
    Remission: "bg-primary text-primary-foreground",
    Stable: "bg-accent text-accent-foreground",
    Worsening: "bg-destructive text-destructive-foreground",
    Relapse: "bg-destructive text-destructive-foreground",
  };

  const removeVisit = async (id) => {
    try {
      setC(await api.deleteVisit(c.id, id));
      toast.success("Visit removed");
    } catch (e) {
      toast.error("Could not remove visit");
    }
  };

  return (
    <Card className="p-7">
      <div className="flex items-start justify-between gap-3">
        <SectionTitle sub="Record follow-up visits over time; the assistant re-assesses the prognosis after each one.">
          Visits & Prognosis Over Time
        </SectionTitle>
        <Button data-testid="record-visit-btn" onClick={() => setOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" /> Record visit
        </Button>
      </div>

      {trends.length > 0 && (
        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="trend-charts">
          {trends.map((t, i) => (
            <div key={i} className="rounded-md border border-border bg-card p-4">
              <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-foreground">
                <TrendingUp className="h-4 w-4 text-primary" /> {t.parameter}
              </p>
              <ResponsiveContainer width="100%" height={160}>
                <LineChart data={t.points} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" width={44} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Line type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ))}
        </div>
      )}

      {timeline.length === 0 ? (
        <EmptyHint>No visits recorded yet. Record the first follow-up visit to track progress.</EmptyHint>
      ) : (
        <div className="flex flex-col gap-4 border-l-2 border-primary/30 pl-6" data-testid="prognosis-timeline">
          {timeline.map((p, i) => {
            const visit = visits.find((v) => v.id === p.visit_id);
            return (
              <div key={p.visit_id || i} className="relative">
                <span className="absolute -left-[31px] top-1 flex h-4 w-4 items-center justify-center rounded-full border-2 border-primary bg-card">
                  <Activity className="h-2.5 w-2.5 text-primary" />
                </span>
                <div className="rounded-md border border-border bg-card p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-heading text-sm font-semibold text-foreground">{p.date}</span>
                      {p.status && <Badge className={statusColor[p.status] || "bg-secondary text-secondary-foreground"}>{p.status}</Badge>}
                    </div>
                    {isDoctor && (
                      <button data-testid={`delete-visit-${p.visit_id}`} onClick={() => removeVisit(p.visit_id)}
                        className="rounded-md p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  {visit?.readings?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {visit.readings.map((r, ri) => (
                        <span key={ri} className="rounded bg-muted px-2 py-0.5 text-xs text-foreground">
                          {r.parameter}: <b>{r.value}</b>
                        </span>
                      ))}
                    </div>
                  )}
                  {visit?.notes && <p className="mt-2 text-sm text-muted-foreground">{visit.notes}</p>}
                  <p className="mt-2 flex items-start gap-1.5 text-sm text-foreground">
                    <TrendingUp className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>{p.outlook}</span>
                  </p>
                  {p.five_year_survival && (
                    <p className="mt-1 text-sm"><span className="font-semibold">5-year outlook: </span>{p.five_year_survival}</p>
                  )}
                  {p.rationale && <p className="mt-1 text-xs text-muted-foreground">{p.rationale}</p>}
                  {p.recommended_changes && (
                    <p className="mt-2 rounded bg-accent/10 px-2 py-1 text-xs text-accent-foreground">
                      <b>Recommended: </b>{p.recommended_changes}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <RecordVisit c={c} setC={setC} open={open} onClose={() => setOpen(false)} />
    </Card>
  );
}

function EmptyHint({ children }) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-dashed border-border bg-muted/40 p-4 text-sm text-muted-foreground">
      <AlertCircle className="h-4 w-4" /> {children}
    </div>
  );
}
